import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { IconPaths, PrescricaoType, showError } from '_metronic/helpers'
import { ItemCatalogo, rotuloCatalogo, selosDoItem } from '../../core/montagem/itens'
import styles from '../../Prescricao.module.scss'
import { Icone } from '../comum/Icone'
import { SelosDoItem } from '../comum/Selos'
import { useRotulos } from '../comum/useRotulos'
import { Button } from 'app/components'

type IProps = {
  item: ItemCatalogo
  atendeSUS: boolean
  adicionado: boolean
  proprio: boolean
  podeInativar: boolean
  alergia?: string
  onAdicionar: () => void
  onInativar: () => Promise<void>
  onTeclaNavegacao: (evento: KeyboardEvent<HTMLButtonElement>) => void
}

/** Uma linha do resultado da busca: o botão principal adiciona; "Inativar" só no item do médico. */
export const ResultadoBusca: FC<IProps> = ({
  item,
  atendeSUS,
  adicionado,
  proprio,
  podeInativar,
  alergia,
  onAdicionar,
  onInativar,
  onTeclaNavegacao,
}) => {
  const { t } = useRotulos()
  const [confirmando, setConfirmando] = useState(false)
  const [inativando, setInativando] = useState(false)
  const cancelarRef = useRef<HTMLButtonElement>(null)

  // A confirmação substitui a linha: o foco vai para "Cancelar", a escolha segura.
  useEffect(() => {
    if (confirmando) cancelarRef.current?.focus()
  }, [confirmando])
  const rotulo = rotuloCatalogo(item)
  const coluna =
    item.categoria === PrescricaoType.Medicamento
      ? item.classe
      : item.categoria === PrescricaoType.Exame
      ? item.tipoExame || t('prescricao.busca.tipo_geral')
      : undefined

  const confirmar = async () => {
    setInativando(true)
    try {
      await onInativar()
    } catch (error) {
      showError(error)
      setInativando(false)
      setConfirmando(false)
    }
  }

  if (confirmando) {
    return (
      <li className={styles.resultado}>
        <div role='alertdialog' aria-label={rotulo} className={styles.confirmacaoInativar}>
          <span className='flex-grow-1'>
            <strong>{t('prescricao.busca.inativar_pergunta', { nome: rotulo })}</strong>{' '}
            {t('prescricao.busca.inativar_explicacao')}
          </span>
          <Button
            size='btn-sm'
            className='btn-secondary'
            onClick={() => setConfirmando(false)}
            disabled={inativando}
            ref={cancelarRef}
          >
            {t('common.cancel')}
          </Button>
          <Button size='btn-sm' className='btn-danger' onClick={confirmar} disabled={inativando}>
            {t('prescricao.busca.inativar')}
          </Button>
        </div>
      </li>
    )
  }

  return (
    <li className={styles.resultado}>
      <Button
        className={`${styles.botaoTexto} ${styles.acaoResultado} d-flex flex-column gap-1`}
        onClick={onAdicionar}
        onKeyDown={onTeclaNavegacao}
        ariaDisabled={adicionado}
        unstyled
      >
        {/* Sem aria-label: o nome vem do conteúdo, para selos e o aviso de alergia serem lidos. */}
        <span className='visually-hidden'>
          {adicionado
            ? t('prescricao.busca.ja_adicionado_prefixo')
            : t('prescricao.busca.adicionar')}{' '}
        </span>
        <span>
          <strong>{item.nome}</strong>
          {item.concentracao && ` - ${item.concentracao}`}
        </span>
        {item.apresentacao && (
          <span className={`fs-7 ${styles.textoSecundario}`}>{item.apresentacao}</span>
        )}
        <span className='d-flex flex-wrap gap-1 align-items-center'>
          <SelosDoItem selos={selosDoItem(item, atendeSUS)} />
          {proprio && (
            <span className={`${styles.selo} ${styles.seloProprio}`}>
              {t('prescricao.busca.cadastrado_por_voce')}
            </span>
          )}
          {alergia && (
            <span
              className={`d-inline-flex align-items-center gap-1 fs-7 fw-bold ${styles.pendenciaErro}`}
            >
              <Icone icone={IconPaths.Alert} />
              {t('prescricao.busca.alergia', { alergia })}
            </span>
          )}
        </span>
      </Button>
      <span className={`fs-7 text-truncate ${styles.textoSecundario}`}>{coluna}</span>
      <span className='d-flex align-items-center justify-content-end gap-3'>
        {podeInativar && (
          <Button
            size='btn-sm'
            className='btn-outline btn-outline-danger py-1 px-2'
            onClick={() => setConfirmando(true)}
            ariaLabel={t('prescricao.busca.inativar_aria', { nome: rotulo })}
          >
            {t('prescricao.busca.inativar')}
          </Button>
        )}
        <span
          aria-hidden
          className={`fw-bold text-nowrap ${styles.comIcone} ${
            adicionado ? styles.estadoOk : styles.estadoAndamento
          }`}
        >
          <Icone icone={adicionado ? IconPaths.Check : IconPaths.Add} />
          {adicionado ? t('prescricao.busca.ja_adicionado') : t('prescricao.busca.adicionar')}
        </span>
      </span>
    </li>
  )
}
